import styled from "styled-components";
import { colors, chartColors } from "../../../Shared/colors";

/**
 * Shared styled-components for the Social Media (Meta leads) screen.
 * Kept in one place so the filter card, stat tiles and table stay visually
 * in step with each other and with the Search Leads screen they sit beside.
 */

export const StatusColors = {
  inCrm: chartColors.success,
  notImported: colors.moderate,
  failed: colors.veryHot,
};

/** Label + colour for each reconciliation state, used by the pill and the tiles. */
export const StatusMeta = {
  inCrm: { label: "In CRM", color: StatusColors.inCrm, bg: "rgba(40, 167, 69, 0.10)" },
  notImported: { label: "Not imported", color: "#8a6400", bg: "rgba(250, 188, 11, 0.16)" },
  failed: { label: "Failed", color: StatusColors.failed, bg: "rgba(232, 11, 11, 0.09)" },
};

/**
 * Plot height shared by both charts on this screen.
 *
 * Defined once so the two cards sit at the same height beside each other. The
 * response chart has a row per employee and would otherwise be as tall as the
 * team is large, leaving the volume chart beside it stranded in whitespace.
 */
export const CHART_PLOT_HEIGHT = 230;

export const Card = styled.div`
  background: ${colors.white};
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 14px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08), 0 1px 3px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  position: relative;
`;

export const AccentCard = styled(Card)`
  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, ${colors.brown}, ${colors.textBlue});
    z-index: 1;
  }
`;

export const CardHeader = styled.div`
  padding: 14px 18px 10px 18px;
  border-bottom: 1px solid ${colors.inputBorderWhite};
  display: flex;
  align-items: center;
  gap: 9px;

  svg {
    color: ${colors.brown};
    font-size: 1.1rem;
  }

  h2 {
    margin: 0;
    font-size: 0.95rem;
    font-weight: 700;
    color: ${colors.text};
  }

  /* A qualifier on the right of the title — what the chart is measured in, or
     how many things it covers. Small and grey so it reads as a caption on the
     heading rather than as a second heading. */
  .unit {
    margin-left: auto;
    font-size: 0.72rem;
    color: ${colors.textLight};
    white-space: nowrap;
  }
`;

export const FormRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
  gap: 0.75rem;
  margin-bottom: 0.8rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }

  @media (min-width: 1240px) {
    grid-template-columns: repeat(4, 1fr);
  }
`;

export const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  min-width: 0;

  ${(props) => (props.$span ? `grid-column: span ${props.$span};` : "")}

  @media (max-width: 1239px) {
    grid-column: auto;
  }
`;

export const FormLabel = styled.label`
  color: ${colors.text};
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.6px;
`;

const fieldBase = `
  padding: 8px 11px;
  height: 38px;
  border-radius: 8px;
  background-color: ${colors.white};
  color: ${colors.text};
  font-size: 0.82rem;
  font-family: inherit;
  outline: none;
  width: 100%;
  min-width: 0;
  transition: all 0.2s ease;
`;

export const TextInput = styled.input`
  ${fieldBase}
  border: 1px solid ${colors.inputBorderWhite};

  &::placeholder {
    color: ${colors.inputFeild};
  }
  &:hover {
    border-color: ${colors.brown};
  }
  &:focus {
    border-color: ${colors.brown};
    box-shadow: 0 0 0 3px rgba(36, 147, 147, 0.14);
  }
`;

export const SelectInput = styled.select`
  ${fieldBase}
  border: 1px solid ${colors.inputBorderWhite};
  cursor: pointer;

  &:hover {
    border-color: ${colors.brown};
  }
  &:focus {
    border-color: ${colors.brown};
    box-shadow: 0 0 0 3px rgba(36, 147, 147, 0.14);
  }
`;

export const Segmented = styled.div`
  display: flex;
  height: 38px;
  border: 1px solid ${colors.inputBorderWhite};
  border-radius: 8px;
  overflow: hidden;

  button {
    flex: 1;
    font-family: inherit;
    font-size: 0.75rem;
    font-weight: 600;
    padding: 0 6px;
    white-space: nowrap;
    border: none;
    border-right: 1px solid ${colors.inputBorderWhite};
    background: ${colors.white};
    color: ${colors.textMed};
    cursor: pointer;
    transition: all 0.18s ease;

    &:last-child {
      border-right: none;
    }
    &:hover {
      background: ${colors.inputWhite};
      color: ${colors.brown};
    }
    &[aria-pressed="true"] {
      background: ${colors.brown};
      color: ${colors.white};
    }
    &:focus-visible {
      outline: 2px solid ${colors.textBlue};
      outline-offset: -2px;
    }
  }
`;

export const ActionRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;
  align-items: center;
  justify-content: space-between;
  border-top: 1px solid ${colors.inputBorderWhite};
  padding-top: 0.8rem;
`;

export const ChipRow = styled.div`
  display: flex;
  gap: 0.4rem;
  flex-wrap: wrap;
`;

export const Chip = styled.button`
  font-family: inherit;
  font-size: 0.75rem;
  font-weight: 600;
  padding: 6px 12px;
  min-height: 32px;
  border-radius: 20px;
  cursor: pointer;
  border: 1px solid ${colors.inputBorderWhite};
  background: ${colors.white};
  color: ${colors.textMed};
  transition: all 0.18s ease;

  &:hover {
    border-color: ${colors.brown};
    color: ${colors.brown};
  }
  &[aria-pressed="true"] {
    background: ${colors.brown};
    border-color: ${colors.brown};
    color: ${colors.white};
  }
  &:focus-visible {
    outline: 2px solid ${colors.brown};
    outline-offset: 2px;
  }
`;

export const PrimaryButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  justify-content: center;
  font-family: inherit;
  font-size: 0.8rem;
  font-weight: 600;
  padding: 9px 18px;
  min-height: 38px;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  color: ${colors.white};
  background: linear-gradient(135deg, ${colors.brown} 0%, #1a6b6b 100%);
  box-shadow: 0 2px 8px rgba(36, 147, 147, 0.3);
  transition: all 0.22s ease;

  svg {
    font-size: 1rem;
  }

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(36, 147, 147, 0.4);
    background: linear-gradient(135deg, #1a6b6b 0%, ${colors.brown} 100%);
  }
  &:active:not(:disabled) {
    transform: translateY(0);
  }
  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
    transform: none;
  }
  &:focus-visible {
    outline: 2px solid ${colors.textBlue};
    outline-offset: 2px;
  }
`;

export const GhostButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  justify-content: center;
  font-family: inherit;
  font-size: 0.8rem;
  font-weight: 600;
  padding: 9px 16px;
  min-height: 38px;
  border-radius: 8px;
  cursor: pointer;
  border: 1px solid ${colors.inputBorderWhite};
  background: ${colors.white};
  color: ${colors.textMed};
  transition: all 0.18s ease;

  svg {
    font-size: 1rem;
  }

  &:hover:not(:disabled) {
    border-color: ${colors.brown};
    color: ${colors.brown};
  }
  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }
  &:focus-visible {
    outline: 2px solid ${colors.brown};
    outline-offset: 2px;
  }
`;
